<template>
  <el-dialog
    width="560px"
    :visible="visible"
    :lock-scroll="false"
    :destroy-on-close="true"
    append-to-body
    custom-class="ele-dialog-form"
    title="订单完成"
    @update:visible="updateVisible">
    <el-form :model="form" label-width="80px" size="small" @keyup.enter.native="save" @submit.native.prevent>
      <el-form-item label="出款银行">
        <el-select
          v-model="form.resource_column_ids"
          clearable
          filterable
          multiple
          class="ele-block"
          placeholder="请选择"
          @change="onSelectChange">
          <el-option
            v-for="item in columnList"
            :key="item.id"
            :value="item.id"
            :label="item.resource_info" />
        </el-select>
      </el-form-item>

      <el-form-item v-if="showSplit" label="拆分明细">
        <div class="split-box">
          <div class="split-head">
            <span class="col-info">付款卡</span>
            <span class="col-amt">处理金额</span>
          </div>
          <div v-for="row in splitRows" :key="row.id" class="split-row">
            <span class="col-info" :title="row.label">{{ row.label }}</span>
            <el-input
              class="col-amt-input"
              size="mini"
              clearable
              placeholder="金额"
              :value="splitAmounts[row.id]"
              @input="onAmountInput(row.id, $event)" />
          </div>
          <div class="split-foot">
            <span>合计 {{ splitUsedText }} / 订单 {{ orderAmountText }}</span>
            <span v-if="splitMismatch" class="split-warn">须等于订单金额</span>
          </div>
        </div>
      </el-form-item>

      <el-form-item label="手动填写">
        <el-input
          v-model.trim="form.resource_manual"
          clearable
          placeholder="请输入"
          @input="onManualInput" />
      </el-form-item>
    </el-form>
    <div slot="footer">
      <el-button @click="updateVisible(false)">取消</el-button>
      <el-button type="primary" :loading="loading" :disabled="submitDisabled" @click="save">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
function money(val) {
  const n = Number(val);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
}

function cardBalance(item) {
  if (!item) return 0;
  const direct = money(item.sj_amount);
  if (direct > 0) return direct;
  const m = String(item.resource_info || '').match(/\(([\d.]+)\)\s*$/);
  return m ? money(m[1]) : 0;
}

function sanitizeAmount(val) {
  return String(val == null ? '' : val)
    .replace(/[^\d.]/g, '')
    .replace(/(\..*)\./g, '$1');
}

export default {
  name: 'ShowOver',
  props: {
    visible: Boolean,
    data: Object
  },
  data() {
    return {
      form: {
        id: null,
        resource_column_ids: [],
        resource_manual: ''
      },
      splitAmounts: {},
      loading: false,
      columnList: []
    };
  },
  computed: {
    orderAmount() {
      return money(this.data && this.data.paymentAmount);
    },
    orderAmountText() {
      return this.orderAmount.toFixed(2);
    },
    selectedIds() {
      return (this.form.resource_column_ids || []).map(Number).filter((id) => id > 0);
    },
    showSplit() {
      return this.selectedIds.length > 1;
    },
    columnMap() {
      const map = {};
      (this.columnList || []).forEach((item) => {
        map[Number(item.id)] = item;
      });
      return map;
    },
    splitRows() {
      return this.selectedIds.map((id) => {
        const item = this.columnMap[id] || {};
        return {
          id,
          label: item.resource_info || String(id),
          balance: cardBalance(item)
        };
      });
    },
    splitUsed() {
      return money(
        this.selectedIds.reduce((sum, id) => sum + money(this.splitAmounts[id]), 0)
      );
    },
    splitUsedText() {
      return this.splitUsed.toFixed(2);
    },
    splitMismatch() {
      return this.showSplit && Math.abs(this.splitUsed - this.orderAmount) > 0.009;
    },
    submitDisabled() {
      return this.splitMismatch;
    }
  },
  watch: {
    data: {
      immediate: true,
      handler() {
        this.resetForm();
      }
    },
    visible(val) {
      if (!val) {
        this.loading = false;
        return;
      }
      this.resetForm();
      this.loadColumns();
    }
  },
  methods: {
    resetForm() {
      const row = this.data || {};
      const presetId = row.resource_column_id && row.resource_column_id !== '-'
        ? Number(row.resource_column_id)
        : 0;
      this.form = {
        id: row.id || null,
        resource_column_ids: presetId > 0 ? [presetId] : [],
        resource_manual: ''
      };
      this.splitAmounts = {};
    },
    // 选卡后按余额贪心预填，可再手改
    autofillAmounts(ids) {
      const next = {};
      let remain = this.orderAmount;
      (ids || []).forEach((rawId) => {
        const id = Number(rawId);
        if (id <= 0) return;
        const bal = cardBalance(this.columnMap[id]);
        let use = 0;
        if (remain > 0.009 && bal > 0) {
          use = money(Math.min(bal, remain));
          remain = money(remain - use);
        }
        next[id] = use > 0 ? use.toFixed(2) : '';
      });
      this.splitAmounts = next;
    },
    onSelectChange(ids) {
      if (ids && ids.length) this.form.resource_manual = '';
      if (!ids || ids.length < 2) {
        this.splitAmounts = {};
        return;
      }
      this.autofillAmounts(ids);
    },
    onManualInput(val) {
      if (val) {
        this.form.resource_column_ids = [];
        this.splitAmounts = {};
      }
    },
    onAmountInput(id, val) {
      this.$set(this.splitAmounts, id, sanitizeAmount(val));
    },
    save() {
      const ids = this.selectedIds;
      const manual = (this.form.resource_manual || '').trim();
      if (!ids.length && !manual) {
        this.$message.warning('请选择');
        return;
      }
      if (this.submitDisabled) {
        this.$message.warning(`拆分合计须等于订单金额 ${this.orderAmountText}`);
        return;
      }

      const payload = { id: this.form.id, order_status: 1 };
      if (ids.length > 1) {
        const items = [];
        for (const id of ids) {
          const amount = money(this.splitAmounts[id]);
          if (amount <= 0) {
            this.$message.warning('请输入');
            return;
          }
          const bal = cardBalance(this.columnMap[id]);
          if (bal > 0 && amount > bal + 0.009) {
            this.$message.warning('处理金额不能超过卡余额');
            return;
          }
          items.push({ resource_column_id: id, amount });
        }
        payload.items = items;
      } else if (ids.length === 1) {
        payload.resource_column_id = ids[0];
      } else {
        payload.remark = manual;
      }

      this.loading = true;
      this.$http
        .post('/Outorder/updateStatus', payload)
        .then((res) => {
          this.loading = false;
          if (res.data.code === 0) {
            this.$message.success(res.data.msg);
            this.updateVisible(false);
            this.$emit('done');
            return;
          }
          this.$message.error(res.data.msg);
        })
        .catch((e) => {
          this.loading = false;
          this.$message.error(e.message);
        });
    },
    updateVisible(value) {
      this.$emit('update:visible', value);
    },
    loadColumns() {
      if (this.columnList.length) {
        if (this.selectedIds.length > 1) this.autofillAmounts(this.selectedIds);
        return;
      }
      this.$http
        .get('/Resourcecolumn/getResourceColumnList')
        .then((res) => {
          if (res.data.code === 0) {
            this.columnList = res.data.data || [];
            if (this.selectedIds.length > 1) {
              this.autofillAmounts(this.selectedIds);
            }
            return;
          }
          this.$message.error(res.data.msg);
        })
        .catch((e) => {
          this.$message.error(e.message);
        });
    }
  }
};
</script>

<style scoped>
.split-box {
  width: 100%;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;
  font-size: 12px;
  line-height: 1.4;
  color: #606266;
}
.split-head,
.split-row,
.split-foot {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
}
.split-head {
  background: #f5f7fa;
  color: #909399;
  font-weight: 500;
}
.split-row + .split-row {
  border-top: 1px solid #ebeef5;
}
.split-foot {
  border-top: 1px solid #ebeef5;
  color: #909399;
  justify-content: flex-end;
  gap: 12px;
}
.split-warn {
  color: #f56c6c;
}
.col-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.col-amt {
  width: 120px;
  flex: 0 0 120px;
  text-align: right;
}
.col-amt-input {
  width: 120px;
  flex: 0 0 120px;
}
</style>
